import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import {
  ScoutJobDetailResponse,
  PublicationPhotosResponse,
} from '@doda/api-client/aspida/@types';
import { LOADING_STATUS } from '@doda/common/src/constants/loadingStatus';
import { FetchBaseStateType } from '@doda/common/src/interfaces/fetchBaseState';
import { TabState } from '@doda/common/src/constants/jobSearchDetailTab';

import {
  getJobSearchDetailById,
  getScoutJobSearchDetailById,
  JobDetailError,
} from '@doda/pc/src/redux/features/jobSearchDetail/services/jobSearchDetailAPI';

import { getCorporatePublishedPhotoUrlByJobId } from '@doda/pc/src/redux/features/companyPublicationPhoto/services/publicationPhotoAPI';
import { RootState } from '@doda/pc/src/redux/store';
import { JobDetail } from '@doda/common/src/types/jobSearchDetail';

type CorporatePublishedPhotoEntityType =
  FetchBaseStateType<PublicationPhotosResponse | null>;

type CorporatePublishedPhotoStateType = CorporatePublishedPhotoEntityType;

type JobSearchDetailEntityType = FetchBaseStateType<
  (JobDetail & ScoutJobDetailResponse) | null
>;

interface JobSearchDetailErrorType {
  error: JobDetailError | null;
}

interface JobSearchDetailTabType {
  isViewedJobDetailTab: boolean;
  isViewedPickUpTab: boolean; // PickUpタブを開いたかどうか
}

type JobSearchDetailInitialStateType = JobSearchDetailEntityType &
  JobSearchDetailErrorType &
  JobSearchDetailTabType;

/**
 * JobDetailParamsの型定義
 */
export interface JobDetailParams {
  from?: string;
  recommendID?: string;
  scoutlistId?: string;
  messageId?: string;
  osscfl?: string;
  usrclk?: string;
  KGMmeasures?: string;
  recordMessageId?: string;
}
type MoveTo = 'prev' | 'next';

/**
 * AdobeAnalyticsの型定義
 */
export interface AdobeAnalytics {
  previousPageName: string;
  previousChannel: string;
}

/**
 * TabDataの型定義
 */
export interface TabData {
  jobId: string;
  tabState: TabState;
  tabHistory: TabState[];
}

// 初期型
/**
 * JobSearchDetailState
 */
export interface JobSearchDetailState {
  isScout: boolean;
  cookieHistoryId: string;
  tabDataList: TabData[];
  moveTo: MoveTo | null;
  jobIds: string[];
  jobSearchDetail: JobSearchDetailInitialStateType;
  params?: JobDetailParams;
  measureData: {
    adobeAnalytics: AdobeAnalytics;
  };
  publicationPhotos: CorporatePublishedPhotoStateType;
  abTestRuijiRecommend: boolean;
}

// 初期状態
export const jobSearchDetailInitialState: JobSearchDetailState = {
  tabDataList: [],
  isScout: false,
  cookieHistoryId: '',
  moveTo: null,
  jobIds: [],
  jobSearchDetail: {
    isViewedJobDetailTab: false, // 求人詳細タブを開いたかどうか
    isViewedPickUpTab: false, // PickUpタブを開いたかどうか
    // その他storeで管理したい状態があれば追記する
    loading: LOADING_STATUS.IDLE,
    entity: null,
    error: null,
  },
  measureData: {
    adobeAnalytics: {
      previousChannel: '',
      previousPageName: '',
    },
  },
  publicationPhotos: {
    loading: LOADING_STATUS.IDLE,
    entity: null,
  },
  abTestRuijiRecommend: false,
};

export const jobSearchDetailSlice = createSlice({
  name: 'jobSearchDetail',
  initialState: jobSearchDetailInitialState,
  reducers: {
    viewedJobDetailTab: (state) => {
      state.jobSearchDetail.isViewedJobDetailTab = true;
    },
    viewedPickUpTab: (state) => {
      state.jobSearchDetail.isViewedPickUpTab = true;
    },
    setAdobeAnalytics: (state, action: PayloadAction<AdobeAnalytics>) => {
      state.measureData.adobeAnalytics = action.payload;
    },
    setIsScout: (state, action: PayloadAction<boolean>) => {
      state.isScout = action.payload;
    },
    setCookieHistoryId: (state, action: PayloadAction<string>) => {
      state.cookieHistoryId = action.payload;
    },
    setTabState: (
      state,
      action: PayloadAction<{ tabState: TabState; jobId: string }>
    ) => {
      if (!action.payload.tabState) return;
      const setTabData: TabData = {
        jobId: action.payload.jobId,
        tabState: action.payload.tabState,
        tabHistory: [
          ...(state.tabDataList.find(
            (tabData) => tabData.jobId === action.payload.jobId
          )?.tabHistory || []),
          action.payload.tabState,
        ],
      };

      state.tabDataList = state.tabDataList.some(
        (tabData) => tabData.jobId === action.payload.jobId
      )
        ? state.tabDataList.map((tabData) =>
            tabData.jobId === action.payload.jobId ? setTabData : tabData
          )
        : [...state.tabDataList, setTabData];
    },
    setJobIndex: (state, action: PayloadAction<string[]>) => {
      state.jobIds = action.payload;
    },
    setJobDetailParams: (state, action: PayloadAction<JobDetailParams>) => {
      state.params = action.payload;
    },
    setJobDetailMoveTo: (state, action: PayloadAction<MoveTo | null>) => {
      state.moveTo = action.payload;
    },
    deleteTabData: (state, action: PayloadAction<string>) => {
      state.tabDataList = state.tabDataList.filter(
        (tabData) => tabData.jobId !== action.payload
      );
    },
    resetJobSearchDetail: (state) => {
      state.jobSearchDetail.entity = null;
    },
    setAbTestRuijiRecommend: (state, action: PayloadAction<boolean>) => {
      state.abTestRuijiRecommend = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getJobSearchDetailById.pending, (state) => {
      state.jobSearchDetail.loading = LOADING_STATUS.PENDING;
    });
    builder.addCase(getJobSearchDetailById.fulfilled, (state, action) => {
      state.jobSearchDetail.loading = LOADING_STATUS.SUCCESS;
      state.jobSearchDetail.entity = action.payload;
      state.jobSearchDetail.error = null;
    });
    builder.addCase(getJobSearchDetailById.rejected, (state, action) => {
      state.jobSearchDetail.loading = LOADING_STATUS.ERROR;
      state.jobSearchDetail.error = action.payload ?? null;
    });
    builder.addCase(getScoutJobSearchDetailById.pending, (state) => {
      state.jobSearchDetail.loading = LOADING_STATUS.PENDING;
    });
    builder.addCase(getScoutJobSearchDetailById.fulfilled, (state, action) => {
      state.jobSearchDetail.loading = LOADING_STATUS.SUCCESS;
      state.jobSearchDetail.entity = action.payload;
      state.jobSearchDetail.error = null;
    });
    builder.addCase(getScoutJobSearchDetailById.rejected, (state, action) => {
      state.jobSearchDetail.loading = LOADING_STATUS.ERROR;
      state.jobSearchDetail.error = action.payload ?? null;
    });
    builder.addCase(
      getCorporatePublishedPhotoUrlByJobId.fulfilled,
      (state, action) => {
        state.publicationPhotos.entity = action.payload;
      }
    );
    builder.addCase(
      getCorporatePublishedPhotoUrlByJobId.rejected,
      (state, action) => {
        state.jobSearchDetail.loading = LOADING_STATUS.ERROR;
        state.jobSearchDetail.error = action.payload ?? null;
      }
    );
  },
});

// Create Action
export const {
  viewedJobDetailTab,
  viewedPickUpTab,
  setAdobeAnalytics,
  setIsScout,
  setCookieHistoryId,
  setTabState,
  setJobIndex,
  setJobDetailParams,
  setJobDetailMoveTo,
  deleteTabData,
  resetJobSearchDetail,
  setAbTestRuijiRecommend,
} = jobSearchDetailSlice.actions;

// Reducer
export const jobSearchDetailReducer = jobSearchDetailSlice.reducer;

// Selector

// TODO: 使われていないので削除する
/**
 * jobSearchDetailSelector
 * @param state
 * @returns jobSearchDetail
 */
export const jobSearchDetailSelector = (
  state: RootState
): typeof jobSearchDetailInitialState => state.jobSearchDetail;

/**
 * jobSearchDetailLoadingSelector
 * @param state
 * @returns jobSearchDetailLoading
 */
export const jobSearchDetailLoadingSelector = (state: RootState) =>
  state.jobSearchDetail.jobSearchDetail.loading;

// TODO: 使われていないので削除する
export const isJobSearchDetailPending = createSelector(
  jobSearchDetailLoadingSelector,
  (state) => state === LOADING_STATUS.PENDING
);

/**
 * jobSearchDetailEntitySelector
 * @param state
 * @returns jobSearchDetailEntity
 */
export const jobSearchDetailEntitySelector = (state: RootState) =>
  state.jobSearchDetail?.jobSearchDetail.entity;

const jobSearchDetailErrorSelector = (state: RootState) =>
  state.jobSearchDetail?.jobSearchDetail.error;

// TODO: 使われていないので削除する
/**
 * jobSearchDetailErrorCodeSelector
 * @param state
 * @returns jobSearchDetailErrorCode
 */
export const jobSearchDetailErrorCodeSelector = (state: RootState) =>
  jobSearchDetailErrorSelector(state)?.code;

// TODO: 使われていないので削除する
/**
 * isJobSearchDetailErrorSelector
 * @param state
 * @returns isJobSearchDetailError
 */
export const isJobSearchDetailErrorSelector = (state: RootState) =>
  jobSearchDetailErrorSelector(state) !== null;

// TODO: 使われていないので削除する
/**
 * jobSearchDetailIsScoutSelector
 * @param state
 * @returns jobSearchDetailIsScout
 */
export const jobSearchDetailIsScoutSelector = (state: RootState) =>
  state.jobSearchDetail.isScout;

// TODO: 使われていないので削除する
/**
 * jobSearchDetailCookieHistoryIdSelector
 * @param state
 * @returns jobSearchDetailCookieHistoryId
 */
export const jobSearchDetailCookieHistoryIdSelector = (state: RootState) =>
  state.jobSearchDetail.cookieHistoryId;

// TODO: 使われていないので削除する
/**
 * jobSearchDetailTabStateSelector
 * @param jobId
 * @returns jobSearchDetailTabState
 */
export const jobSearchDetailTabStateSelector =
  (jobId: string) => (state: RootState) => {
    return (
      state.jobSearchDetail.tabDataList.find(
        (tabData) => tabData.jobId === jobId
      )?.tabState || 'requirement'
    );
  };

// TODO: 使われていないので削除する
export const currentDisplayJobIdSelector = createSelector(
  jobSearchDetailEntitySelector,
  (state) => {
    if (!state?.job) return null;
    return state.job.jid;
  }
);

// TODO: 使われていないので削除する
/**
 * jobSearchDetailIdsSelector
 * @param state
 * @returns jobSearchDetailIds
 */
export const jobSearchDetailIdsSelector = (state: RootState) =>
  state.jobSearchDetail.jobIds;

// TODO: 使われていないので削除する
/**
 * jobDetailParamsSelector
 * @param state
 * @returns jobDetailParams
 */
export const jobDetailParamsSelector = (state: RootState) =>
  state.jobSearchDetail.params;

// TODO: 使われていないので削除する
/**
 * jobDetailMoveToSelector
 * @param state
 * @returns jobDetailMoveTo
 */
export const jobDetailMoveToSelector = (state: RootState) =>
  state.jobSearchDetail.moveTo;

// TODO: 使われていないので削除する
/**
 * jobSearchDetailTabHistorySelector
 * @param jobId
 * @returns jobSearchDetailTabHistory
 */
export const jobSearchDetailTabHistorySelector =
  (jobId: string) => (state: RootState) =>
    state.jobSearchDetail.tabDataList.find((tabData) => tabData.jobId === jobId)
      ?.tabHistory || [];

// TODO: 使われていないので削除する
/**
 * jobSearchDetailAdobeAnalyticsSelector
 * @param state
 * @returns jobSearchDetailAdobeAnalytics
 */
export const jobSearchDetailAdobeAnalyticsSelector = (state: RootState) =>
  state.jobSearchDetail.measureData.adobeAnalytics;

// TODO: 使われていないので削除する
/**
 * jobSearchDetailRecuruitPrImageSelector
 * @param state
 * @returns jobSearchDetailRecuruitPrImage
 */
export const jobSearchDetailRecuruitPrImageSelector = (state: RootState) =>
  state.jobSearchDetail.jobSearchDetail.entity?.job?.recuruitPrImage;

// TODO: 使われていないので削除する
/**
 * jobSearchDetailWcnsIdSelector
 * @param state
 * @returns jobSearchDetailWcnsId
 */
export const jobSearchDetailWcnsIdSelector = (state: RootState) =>
  state.jobSearchDetail.jobSearchDetail.entity?.job?.wcnsId;

// TODO: 使われていないので削除する
export const jobSearchDetailWmuidSelector = createSelector(
  jobSearchDetailEntitySelector,
  (state) => {
    if (!state?.job) return null;
    return state.job.wmuid;
  }
);

// TODO: 使われていないので削除する
/**
 * jobSearchDetailSeminarTitleIdSelector
 * @param state
 * @returns jobSearchDetailSeminarTitleId
 */
export const jobSearchDetailSeminarTitleIdSelector = (state: RootState) =>
  state.jobSearchDetail.jobSearchDetail.entity?.seminar?.seminarTitleId;

// TODO: 使われていないので削除する
/**
 * jobSerchDetailJobOfferKilndSelector
 * @param state
 * @returns jobSerchDetailJobOfferKilnd
 */
export const jobSerchDetailJobOfferKilndSelector = (state: RootState) =>
  state.jobSearchDetail.jobSearchDetail.entity?.job?.jobOfferKind;

/**
 * Selector 求人詳細タブを開いたかどうかを管理。ローカルストレージのviewCount更新用。
 * @param state
 * @returns isViewedJobDetailTab
 */
export const isViewedJobDetailTabSelector = (state: RootState) =>
  state.jobSearchDetail.jobSearchDetail.isViewedJobDetailTab;

/**
 * Selector PickUpタブを開いたかどうかを管理
 * @param state
 * @returns isViewedPickUpTab
 */
export const isViewedPickUpTabSelector = (state: RootState) =>
  state.jobSearchDetail.jobSearchDetail.isViewedPickUpTab;

/**
 * corporateClientPhotoSelector
 * @param state
 * @returns corporateClientPhoto
 */
export const corporateClientPhotoSelector = (state: RootState) =>
  state.jobSearchDetail.publicationPhotos.entity?.photoUrls;

/**
 * corporateClientPhotosLatestUpdateDateSelector
 * @param state
 * @returns corporateClientPhoto
 */
export const corporateClientPhotosLatestUpdateDateSelector = (
  state: RootState
) => state.jobSearchDetail.publicationPhotos.entity?.latestUpdateDate;

// TODO: 使われていないので削除する
/**
 * abTestRuijiRecommendSelector
 * @param state
 * @returns abTestRuijiRecommend
 */
export const abTestRuijiRecommendSelector = (state: RootState) =>
  state.jobSearchDetail.abTestRuijiRecommend;
